import { ContentLink, useContentSingletons, usePageContent } from "@robe/client/xcore-ui";
import { Box, Flex, SimpleGrid } from "@xcorejs/ui";
import ProductCard, { ProductCardPop, ProductCardProductOrAccessory } from "design-system/robe/Cards/Product";
import ProductOverviewCard, { ProductOverviewCardValue } from "design-system/robe/Cards/ProductOverview";
import { Accessory, Product, ProductOverviewPage, Products } from "xcore/types";
import OverviewText from "./OverviewText";
import ProductFilters from "./ProductFilters";
import { useLayout } from "xcore";
import { Heading3 } from "design-system";
import { Fragment } from "react";
import { useRouter } from "next/router";

const ProductOverviewView = () => {
  const { localize } = useLayout();
  const { values } = usePageContent<ProductOverviewPage>();
  const [productsContentType] = useContentSingletons<[Products]>();
  const items = localize(values?.itemsRelation) ?? localize(productsContentType?.values?.itemsRelation);
  const { query } = useRouter();
  const isPopDetail = !!query.id;

  // Filter out discontinued products and accessories if the pop setting is enabled
  const filteredItems =
    items?.filter(
      values.hideDiscontinued
        ? (i) =>
          i.type === "product" || i.type === "accessory"
            ? !localize((i as Product | Accessory).values.discontinuedReg) && !(i as Product | Accessory).values.discontinuedGlob
            : true
        : (_) => true
    ) ?? [];

  const products = (filteredItems?.filter((item) => item.type === "product" || item.type === "accessory") as Product[] | Accessory[]) ?? [];

  const pops = (items?.filter((item) => item.type === "productOverviewPage") as ProductOverviewPage[]) ?? [];

  const tilesLayout = values?.tilesLayout;
  const popHtml = localize(values.html);
  const featuredCategoriesHeading = localize(productsContentType.values?.featuredCategoriesHeading);

  return (
    <>
      {!values.hideFilters && <ProductFilters />}

      <OverviewText />

      {popHtml && (
        <Box mt={{ _: "4rem" }}>
          <div dangerouslySetInnerHTML={{ __html: popHtml }}></div>
        </Box>
      )}

      {items && items.length > 0 && (
        <Flex flexDirection={values && localize(values.showPages) ? "column" : "column-reverse"}>
          <Box mt="6rem">
            {featuredCategoriesHeading && !isPopDetail && (
              <Heading3 textAlign="center" fontWeight={600} mb="3rem">
                {featuredCategoriesHeading}
              </Heading3>
            )}

            {(tilesLayout === "showPOPsFirstProductsBellow" || tilesLayout === "showProductsFirstPOPsBellow") && (
              <>
                <SimpleGrid
                  gap={{ _: "1.5rem", sm: "3rem" }}
                  columns={{ _: 2, sm: 4 }}
                  column="minmax(100px, 300px)"
                  width={{ _: "auto", lg: "100%" }}
                  marginBottom={{ _: "1.5rem", sm: "3rem" }}
                >
                  {tilesLayout === "showPOPsFirstProductsBellow"
                    ? pops.map((item) => (
                      <ContentLink
                        key={item.id}
                        content={item}
                        variant="simple"
                        overflow="hidden"
                        style={{ width: "100%", height: "100%" }}
                      >
                        <ProductOverviewCard value={item} />
                      </ContentLink>
                    ))
                    : tilesLayout === "showProductsFirstPOPsBellow"
                      ? products.map((item) => (
                        <ContentLink
                          key={item.id}
                          content={item}
                          variant="simple"
                          overflow="hidden"
                          style={{ width: "100%", height: "100%" }}
                        >
                          <ProductCard product={item} />
                        </ContentLink>
                      ))
                      : null}
                </SimpleGrid>

                <SimpleGrid
                  gap={{ _: "1.5rem", sm: "3rem" }}
                  columns={{ _: 2, sm: 4 }}
                  column="minmax(100px, 300px)"
                  width={{ _: "auto", lg: "100%" }}
                >
                  {tilesLayout === "showProductsFirstPOPsBellow" &&
                    pops.map((item) => (
                      <ContentLink
                        key={item.id}
                        content={item}
                        variant="simple"
                        overflow="hidden"
                        style={{ width: "100%", height: "100%" }}
                      >
                        <ProductOverviewCard value={item} />
                      </ContentLink>
                    ))}
                  {tilesLayout === "showPOPsFirstProductsBellow" &&
                    products.map((item) => (
                      <ContentLink
                        key={item.id}
                        content={item}
                        variant="simple"
                        overflow="hidden"
                        style={{ width: "100%", height: "100%" }}
                      >
                        <ProductCard product={item} />
                      </ContentLink>
                    ))}
                </SimpleGrid>
              </>
            )}

            {tilesLayout !== "showPOPsFirstProductsBellow" && tilesLayout !== "showProductsFirstPOPsBellow" && (
              <SimpleGrid
                gap={{ _: "1.5rem", sm: "3rem" }}
                columns={{ _: 2, sm: 4 }}
                column="minmax(100px, 300px)"
                width={{ _: "auto", lg: "100%" }}
              >
                {filteredItems.map((item) => {
                  if (!item?.slug || !item?.type) {
                    return <Fragment key={item.id} />;
                  }

                  return (
                    <ContentLink
                      key={item.id}
                      content={{ id: item.id, type: item.type, slug: item.slug }}
                      variant="simple"
                      style={{ width: "100%", height: "100%" }}
                      overflow="hidden"
                    >
                      {item.type === "product" || item.type === "accessory" ? (
                        <ProductCard product={item as ProductCardProductOrAccessory} />
                      ) : tilesLayout === "showPOPsAsProducts" ? (
                        <ProductCard pop={item as ProductCardPop} />
                      ) : (
                        <ProductOverviewCard value={item as ProductOverviewCardValue} />
                      )}
                    </ContentLink>
                  );
                })}
              </SimpleGrid>
            )}
          </Box>
        </Flex>
      )}
    </>
  );
};

export default ProductOverviewView;
